<template>
  <div class="layout-columns-aside">
    <el-scrollbar>
      <layout-logo />
      <ul ref="listRef">
        <li
          v-for="(v, k) in columnsNavList"
          :key="v.path"
          class="layout-columns"
          :class="{ 'layout-columns-active': k === activeColumnIndex }"
          :title="v.title"
          @click="onColumnClick(v)"
       >
          <div :class="columnsLayoutClass">
            <menu-icon v-if="v.icon" :icon="v.icon" class="columns-menu-icon" />
            <div class="font13">
              {{ v.title && v.title.length >= 4 ? v.title.substr(0, 4) : v.title }}
            </div>
          </div>
        </li>
        <div
          v-if="columnsNavList.length"
          ref="activeRef"
          :class="activeSlideClass"
          :style="{ top: `${activeTop}px` }"
       />
      </ul>
    </el-scrollbar>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import useCrmebMenuStore from '@/store/modules/menu';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import type { CrmebNavMenuItem } from '@/utils/crmebMenu';
import LayoutLogo from '../logo/index.vue';
import MenuIcon from '../components/MenuIcon.vue';

const route = useRoute();
const crmebMenu = useCrmebMenuStore();
const themeStore = useCrmebThemeConfigStore();

const listRef = ref<HTMLElement | null>(null);
const activeRef = ref<HTMLElement | null>(null);
const activeTop = ref(2);

const columnsNavList = computed(() => crmebMenu.columnsNavList);
const activeColumnIndex = computed(() => crmebMenu.activeColumnIndex);

const columnsLayoutClass = computed(() =>
  themeStore.themeConfig.columnsAsideLayout === 'columns-horizontal'
    ? 'columns-horizontal'
    : 'columns-vertical',
);

const activeSlideClass = computed(() =>
  themeStore.themeConfig.columnsAsideStyle === 'columns-round'
    ? 'columns-round'
    : 'columns-card',
);

function onColumnClick(v: CrmebNavMenuItem) {
  crmebMenu.onColumnClick(v);
  nextTick(updateActiveSlide);
}

function updateActiveSlide() {
  const ul = listRef.value;
  if (!ul) return;
  const items = ul.querySelectorAll('li.layout-columns');
  const el = items[crmebMenu.activeColumnIndex] as HTMLElement | undefined;
  if (el) {
    activeTop.value = el.offsetTop + (themeStore.themeConfig.columnsAsideStyle === 'columns-round' ? 2 : 0);
  }
}

watch(
  () => route.path,
  () => {
    crmebMenu.syncLayoutByRoute(route);
    nextTick(updateActiveSlide);
  },
  { immediate: true },
);

watch(activeColumnIndex, () => nextTick(updateActiveSlide));
</script>

<style scoped lang="scss">
.layout-columns-aside {
  width: 80px;
  height: 100%;
  background: var(--prev-bg-columnsMenuBar);
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  border-right: 1px solid var(--prev-border-color-lighter);
  z-index: 120;

  ul {
    position: relative;
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      color: var(--prev-bg-columnsMenuBarColor, #e6e6e6);
      width: 100%;
      height: 55px;
      text-align: center;
      display: flex;
      cursor: pointer;
      position: relative;
      z-index: 1;
      i,
      .columns-menu-icon,
      .font13 {
        color: var(--prev-bg-columnsMenuBarColor, #e6e6e6) !important;
      }
      .columns-menu-icon {
        font-size: 18px;
        margin-right: 0;
      }
      .columns-vertical {
        margin: auto;
      }
      .columns-horizontal {
        display: flex;
        flex-direction: row;
        height: 50px;
        width: 100%;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
        .columns-menu-icon {
          margin-right: 3px;
          margin-bottom: 0;
          flex-shrink: 0;
        }
        .font13 {
          flex-shrink: 0;
          line-height: 1.2;
        }
      }
    }
    .layout-columns-active {
      color: var(--prev-bg-columnsMenuActiveColor, #fff);
      i,
      .columns-menu-icon,
      .font13 {
        color: var(--prev-bg-columnsMenuActiveColor, #fff) !important;
      }
    }
    .columns-round {
      background: var(--prev-color-primary);
      position: absolute;
      left: 50%;
      height: 50px;
      width: 65px;
      transform: translateX(-50%);
      z-index: 0;
      transition: 0.3s ease-in-out;
      border-radius: 5px;
      pointer-events: none;
    }
    .columns-card {
      @extend .columns-round;
      width: 100%;
      border-radius: 0;
    }
  }
}
.font13 {
  font-size: 13px;
}
</style>
